import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  Brush,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { Telemetry } from '@bridgewerk/shared';
import { CHART_AXIS_COLOR, CHART_GRID_COLOR, ChartShell } from './ChartShell.js';
import { ChartTooltip } from './ChartTooltip.js';

const COLOR = '#F4803C';
const FILL_ID = 'oee-fill';

export interface OEEChartProps {
  readonly points: Telemetry[] | undefined;
  readonly loading?: boolean;
}

export function OEEChart({ points, loading }: OEEChartProps): JSX.Element {
  const data = useMemo(() => toChartData(points ?? []), [points]);
  return (
    <ChartShell
      title="OEE %"
      accent={COLOR}
      loading={loading && !points}
      empty={!loading && data.length === 0}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={FILL_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLOR} stopOpacity={0.5} />
              <stop offset="100%" stopColor={COLOR} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={CHART_GRID_COLOR} strokeDasharray="3 3" />
          <XAxis
            dataKey="t"
            type="number"
            domain={['dataMin', 'dataMax']}
            scale="time"
            tickFormatter={tickTime}
            stroke={CHART_AXIS_COLOR}
            tick={{ fill: CHART_AXIS_COLOR, fontSize: 10 }}
          />
          <YAxis
            domain={[0, 100]}
            stroke={CHART_AXIS_COLOR}
            tick={{ fill: CHART_AXIS_COLOR, fontSize: 10 }}
            width={48}
          />
          <Tooltip content={<ChartTooltip unit="%" />} />
          <Area
            type="monotone"
            dataKey="v"
            stroke={COLOR}
            strokeWidth={1.5}
            fill={`url(#${FILL_ID})`}
            isAnimationActive={false}
          />
          <Brush
            dataKey="t"
            height={18}
            stroke={CHART_AXIS_COLOR}
            travellerWidth={8}
            fill="transparent"
            tickFormatter={tickTime}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartShell>
  );
}

function toChartData(points: Telemetry[]): { t: number; v: number }[] {
  return points
    .map((p) => ({ t: new Date(p.timestamp).getTime(), v: p.oee_percent }))
    .sort((a, b) => a.t - b.t);
}

function tickTime(value: number): string {
  const d = new Date(value);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
